import { createTranslationHook } from '../MasterDocument/i18nFactory';
import type { TranslateFn } from '../MasterDocument/i18nFactory';

/**
 * i18n del MatchModal.
 *
 * Sigue el mismo patrón que el resto del kit: `createTranslationHook` resuelve
 * contra el motor del host y cae al diccionario local del idioma activo cuando
 * la clave no está registrada. Claves en formato plano (no anidado).
 *
 * La factory vive en `MasterDocument/i18nFactory` pero no es privada de ese
 * feature: el kit la exporta públicamente como factory compartida
 * (`src/index.ts`). Se reutiliza aquí para no duplicar la lógica de precedencia
 * motor → diccionario → respaldo, que ya fue auditada.
 *
 * ## Título con y sin `fieldName`
 *
 * Hay dos claves de título, no una con placeholder opcional:
 *
 * - `match_modal.title` — se usa cuando el consumidor pasa `fieldName`. El
 *   componente interpola el nombre **ya en minúsculas**, para preservar el texto
 *   que el modal venía mostrando.
 * - `match_modal.titleFallback` — se usa cuando `fieldName` no llega. Antes ese
 *   caso lanzaba un `TypeError` que tumbaba la vista completa; ahora degrada a
 *   un encabezado genérico y el modal sigue siendo utilizable.
 *
 * Una sola clave con `{fieldName}` vacío dejaría la frase colgando («…en el »),
 * que en ningún idioma es aceptable.
 *
 * @see src/components/LanguageManagement/README.md - Arquitectura de traducción
 * @see MatchModal.types - `viewLabel` / `continueLabel` para sobrescribir sin i18n
 *
 * @example
 * ```ts
 * const { t } = useMatchModalTranslation();
 * t('match_modal.title', { fieldName: 'nombre' });
 * t('match_modal.view');
 * ```
 */

/** Diccionario base del kit (idioma base: español). */
export const MATCH_MODAL_TRANSLATIONS_ES: Record<string, string> = {
  'match_modal.title': 'Hemos detectado algunas coincidencias en el {fieldName}',
  'match_modal.titleFallback': 'Hemos detectado algunas coincidencias',
  'match_modal.view': 'Ver',
  'match_modal.continue': 'Continuar creando',
};

/** Diccionario en inglés. */
export const MATCH_MODAL_TRANSLATIONS_EN: Record<string, string> = {
  'match_modal.title': 'We found some matches in the {fieldName}',
  'match_modal.titleFallback': 'We found some matches',
  'match_modal.view': 'View',
  'match_modal.continue': 'Continue creating',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const MATCH_MODAL_TRANSLATIONS = {
  es: MATCH_MODAL_TRANSLATIONS_ES,
  en: MATCH_MODAL_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type MatchModalT = TranslateFn;

/**
 * Hook de traducción del MatchModal. Usa `useTranslation` del kit; si la clave
 * no está registrada en el host, cae al diccionario local del idioma activo.
 *
 * No requiere `TranslationProvider`: sin él, `useTranslation` aplica su política
 * de fallo amistoso y el hook resuelve contra los diccionarios locales.
 */
export const useMatchModalTranslation = createTranslationHook(MATCH_MODAL_TRANSLATIONS);
